import React, {MouseEvent, useState} from 'react'
import {Box, Button, Popover} from '@mui/material'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import {useTranslation} from 'react-i18next'
import IconButtonCustom from '@ui/buttons/iconButtonCustom/IconButtonCustom'
import {
	DEFAULT_CUSTOM_MENU_COLOR,
	PICKER_MENU_COLORS,
} from '@containers/accountStyle/constants/palette'
import {buildPanelGradient, isCustomMenuColor} from '@containers/accountStyle/helpers/buildAccountStyleTokens'
import {MenuColor} from '@containers/accountStyle/types'

const sx = {
	grid: {
		display: 'grid',
		// 20 ready-made colours and the custom one: three full rows of seven.
		gridTemplateColumns: 'repeat(7, 28px)',
		gap: '10px',
		padding: '12px',
	},
	swatch: {
		position: 'relative',
		overflow: 'hidden',
		width: '28px',
		height: '28px',
		padding: 0,
		borderRadius: '50%',
		border: '2px solid transparent',
		boxShadow: '0 0 0 1px rgba(0, 0, 0, .15)',
		cursor: 'pointer',
		transition: 'transform .15s',
		'&:hover': {
			transform: 'scale(1.1)',
		},
		'&.selected': {
			borderColor: '#fff',
			boxShadow: (theme: any) => `0 0 0 2px ${theme.palette.text.primary}`,
		},
	},
	custom: {
		background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
		'& input': {
			position: 'absolute',
			inset: 0,
			width: '100%',
			height: '100%',
			opacity: 0,
			cursor: 'pointer',
		},
	},
	reset: {
		gridColumn: '1 / -1',
		textTransform: 'none',
	},
}

interface IProps {
	value: MenuColor | null,
	onSelect: (color: MenuColor | null) => void,
	// Called on every move of the custom colour input; debounce on the caller's side if needed.
	onCustomChange: (color: string) => void,
	tooltip: string,
	// Shown as a button under the swatches that selects null; no button without it.
	resetLabel?: string,
	// The palette icon; white suits the coloured app header.
	iconColor?: string,
}

// The palette button and its swatches. Where the colour goes is up to the caller: the account
// menu saves it in track-api, the sign in page keeps it in the browser.
const MenuColorPicker = ({value, onSelect, onCustomChange, tooltip, resetLabel, iconColor = '#fff'}: IProps) => {
	const {t} = useTranslation()
	const [anchor, setAnchor] = useState<HTMLElement | null>(null)

	const select = (color: MenuColor | null) => {
		setAnchor(null)
		if (color !== value) {
			onSelect(color)
		}
	}

	// A ready-made swatch saved as #rrggbb (teal) is not the custom colour.
	const customSelected = isCustomMenuColor(value) && !PICKER_MENU_COLORS.some((color) => color.value === value)

	return (
		<>
			<IconButtonCustom
				variant={'empty'}
				tooltip={tooltip}
				onClick={(e) => setAnchor((e as MouseEvent<HTMLElement>).currentTarget)}
			>
				<PaletteOutlinedIcon fontSize={'small'} sx={{color: iconColor}}/>
			</IconButtonCustom>

			<Popover
				open={!!anchor}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
				transformOrigin={{vertical: 'top', horizontal: 'right'}}
			>
				<Box sx={sx.grid}>
					{PICKER_MENU_COLORS.map((color) => (
						<Box
							key={color.value}
							component={'button'}
							aria-label={color.label}
							className={color.value === value ? 'selected' : undefined}
							onClick={() => select(color.value)}
							sx={{...sx.swatch, background: buildPanelGradient(color.hex)}}
						/>
					))}

					<Box
						component={'label'}
						title={t('base.menuColorCustom')}
						className={customSelected ? 'selected' : undefined}
						sx={{...sx.swatch, ...sx.custom}}
					>
						<input
							type={'color'}
							defaultValue={customSelected ? value : DEFAULT_CUSTOM_MENU_COLOR}
							onChange={(e) => onCustomChange(e.target.value)}
						/>
					</Box>

					{resetLabel && (
						<Button
							size={'small'}
							variant={value ? 'outlined' : 'contained'}
							onClick={() => select(null)}
							sx={sx.reset}
						>
							{resetLabel}
						</Button>
					)}
				</Box>
			</Popover>
		</>
	)
}

export default MenuColorPicker
